import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cognitoAuthService } from '../services/cognitoAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';

const EyeIcon = ({ open }: { open: boolean }) =>
  open ? (
    <svg width='18' height='18' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
      <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
      <circle cx='12' cy='12' r='3' />
    </svg>
  ) : (
    <svg width='18' height='18' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
      <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
      <line x1='1' y1='1' x2='23' y2='23' />
    </svg>
  );

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const history = useHistory();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendSuccess, setResendSuccess] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t('validation.emailRequired'));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError(t('validation.emailInvalid'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await cognitoAuthService.forgotPassword(email);
      if (result.success) {
        setStep('code');
      } else {
        setError(result.message as string);
      }
    } catch {
      setError(t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) {
      setError(t('resetPassword.codeRequired'));
      return;
    }
    if (!newPassword) {
      setError(t('validation.passwordRequired'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('validation.passwordLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('validation.passwordsMismatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await cognitoAuthService.confirmPassword(email, code, newPassword);
      if (result.success) {
        history.push('/login');
      } else {
        setError(result.message as string);
      }
    } catch {
      setError(t('resetPassword.resetError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    setResendSuccess('');
    try {
      const result = await cognitoAuthService.forgotPassword(email);
      if (result.success) {
        setResendSuccess(t('messages.codeSent'));
      } else {
        setError(result.message as string);
      }
    } catch {
      setError(t('resetPassword.resendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: 'rgb(8 6 20 / 100%)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header variant='default' />
      <div
        className='flex items-center justify-center background-login'
        style={{ paddingTop: '80px', flex: 1 }}
      >
        <div className='login-background-overlay'></div>
        <div className='max-w-md w-full mx-4 animate-fade-in' style={{ zIndex: 2 }}>
          <div className='dark-card'>
            <div className='text-center' style={{ marginBottom: '28px' }}>
              <h1
                style={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: '6px',
                  fontFamily: 'Lexend Deca, sans-serif',
                }}
              >
                {t('forgotPassword.title')}
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                {step === 'email'
                  ? t('forgotPassword.subtitle')
                  : t('resetPassword.subtitle')}
              </p>
            </div>

            {error && (
              <div className='auth-error animate-fade-in'>
                <svg width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24' style={{ flexShrink: 0, marginTop: '1px' }}>
                  <circle cx='12' cy='12' r='10' />
                  <line x1='12' y1='8' x2='12' y2='12' />
                  <line x1='12' y1='16' x2='12.01' y2='16' />
                </svg>
                {error}
              </div>
            )}

            {resendSuccess && (
              <div className='auth-success animate-fade-in'>
                {resendSuccess}
              </div>
            )}

            {step === 'email' ? (
              <form onSubmit={handleSendCode} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label htmlFor='fp-email' className='auth-label'>
                    {t('login.email')}
                  </label>
                  <input
                    id='fp-email'
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    className='input w-full'
                    autoFocus
                    autoComplete='email'
                  />
                </div>
                <button
                  type='submit'
                  disabled={loading}
                  className='btn btn-secondary btn-block'
                >
                  {loading ? (
                    <>
                      <span className='spinner' style={{ width: 16, height: 16, borderWidth: 2, marginRight: 8 }}></span>
                      {t('forgotPassword.sending')}
                    </>
                  ) : (
                    t('forgotPassword.submit')
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label className='auth-label'>{t('login.email')}</label>
                  <input
                    type='email'
                    value={email}
                    className='input w-full'
                    disabled
                    aria-readonly='true'
                    style={{ opacity: 0.5 }}
                  />
                </div>

                <div>
                  <label htmlFor='fp-code' className='auth-label'>
                    {t('register.verificationCode')}
                  </label>
                  <input
                    id='fp-code'
                    type='text'
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={t('resetPassword.codePlaceholder')}
                    className='input w-full'
                    autoFocus
                    autoComplete='one-time-code'
                    inputMode='numeric'
                    maxLength={6}
                    style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '0.2em' }}
                  />
                  {t('resetPassword.codeHint') && (
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                      {t('resetPassword.codeHint')}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor='fp-new-password' className='auth-label'>
                    {t('resetPassword.newPasswordPlaceholder')}
                  </label>
                  <div className='input-wrapper'>
                    <input
                      id='fp-new-password'
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('resetPassword.newPasswordPlaceholder')}
                      className='input w-full'
                      autoComplete='new-password'
                    />
                    <button
                      type='button'
                      className='password-toggle'
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showNewPassword} />
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor='fp-confirm-password' className='auth-label'>
                    {t('resetPassword.confirmPlaceholder')}
                  </label>
                  <div className='input-wrapper'>
                    <input
                      id='fp-confirm-password'
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('resetPassword.confirmPlaceholder')}
                      className='input w-full'
                      autoComplete='new-password'
                    />
                    <button
                      type='button'
                      className='password-toggle'
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showConfirmPassword} />
                    </button>
                  </div>
                </div>

                <button
                  type='submit'
                  disabled={loading}
                  className='btn btn-secondary btn-block'
                >
                  {loading ? (
                    <>
                      <span className='spinner' style={{ width: 16, height: 16, borderWidth: 2, marginRight: 8 }}></span>
                      {t('resetPassword.processing')}
                    </>
                  ) : (
                    t('resetPassword.submit')
                  )}
                </button>

                <div className='text-center'>
                  <button
                    type='button'
                    onClick={handleResendCode}
                    disabled={loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: '4px',
                    }}
                  >
                    {t('resetPassword.resendCode')}
                  </button>
                </div>
              </form>
            )}

            <div className='text-center' style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
                {t('forgotPassword.rememberedPassword')}{' '}
                <Link to='/login' style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                  {t('login.title')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
